"use client";

import { CloseButton } from "@commerce-frontend/ui/base/CloseButton/CloseButton";
import { Icon } from "@commerce-frontend/ui/base/Icon/Icon";
import Stars from "@commerce-frontend/ui/components/Stars/Stars";
import { TaxedPrice } from "@commerce-frontend/ui/components/taxed-price/taxed-price";
import { cn } from "@commerce-frontend/ui/utils/classNames";
import type { Locale } from "@commerce-shared/site-config";
import { countryOf, languageOf } from "@commerce-shared/utility";
import { sendGTMEvent } from "@next/third-parties/google";
import { Command } from "cmdk";
import type { FragmentOf } from "gql.tada";
import { useTranslations } from "next-intl";
import Image from "next/image";
import { useRouter } from "next/navigation";
import { Fragment, memo } from "react";
import type {
	CardProductFragment,
	CardProductVariantFragment,
} from "~/components/product/ProductCard/ProductCard.fragments";
import { useStaticUrl } from "~/hooks/static-url";
import { generateItemDataFromProduct } from "~/lib/gtm";
import { getVariantUrl } from "~/lib/links";
import type {
	CategoryPageResultFragment,
	ContentPageResultFragment,
} from "~/lib/queries/graphql/fragments";
import type { SearchResultObject } from "../SearchBarGroup/SearchBarGroup";

type Props = {
	locale: Locale;
	value: string;
	recentSearchTerms: string[];
	searchResult: SearchResultObject;
	loadingSearchResults: boolean;
	onSelectLink(
		value: string,
		shouldAddToRecentSearchTerms: boolean,
		shouldCloseSearch?: boolean,
	): void;
	onSelectProductLink(): void;
	onClickDeleteLink(value: string): void;
};

export const QuickSearch = memo(
	({
		locale,
		value,
		recentSearchTerms,
		searchResult,
		loadingSearchResults,
		onSelectLink,
		onSelectProductLink,
		onClickDeleteLink,
	}: Props) => {
		const t = useTranslations("QuickSearch");
		const availabilityT = useTranslations("Availability");

		const router = useRouter();
		const searchUrl = useStaticUrl("search");
		const productUrl = useStaticUrl("p/[slug]");

		// Local variables for search results
		const badges = searchResult?.badges;
		const searchTerms = searchResult?.searchTerms;
		const categories = searchResult?.categories;
		const products = searchResult?.products;
		const pages = searchResult?.pages;

		// Determine which search results should be shown
		const hasBadges = badges && badges.length > 0;
		const hasRecentSearchTerms =
			recentSearchTerms && recentSearchTerms.length > 0;
		const hasSearchTerms = searchTerms && searchTerms.length > 0;
		const hasCategories = categories && categories.length > 0;
		const hasProducts = products && products.length > 0;
		const hasPages = pages && pages.length > 0;

		return (
			<>
				{[
					value && (
						<QuickSearchNoResult
							value={value}
							onSelect={() => {
								onSelectLink(value, true);
								router.push(createSearchUrl(value, searchUrl));
							}}
						/>
					),
					hasBadges && (
						<Command.Group
							className="px-4 py-8 text-base font-bold text-main"
							heading={t("popularSearchTerms")}
							aria-label={t("accessibilityBadges")}
						>
							<div className="mt-4 flex flex-row flex-wrap items-center justify-start gap-2">
								{badges.map((badge, index) => (
									<QuickSearchBadge
										key={index}
										badge={badge.value}
										id={badge.id}
										index={index}
										onSelect={() => {
											if (!loadingSearchResults) {
												onSelectLink(badge.value, true);
												router.push(createSearchUrl(badge.value, searchUrl));
											}
										}}
										algoliaQueryId={searchResult.algoliaQueryId}
									/>
								))}
							</div>
						</Command.Group>
					),
					hasRecentSearchTerms && (
						<Command.Group
							className="px-4 py-8 text-base font-bold text-main"
							heading={t("searchTerms")}
							aria-label={t("accessibilityRecentSearchTerms")}
						>
							<div className="mt-2 flex flex-col gap-2">
								{recentSearchTerms.map((term, index) => (
									<QuickSearchRecentSearchTerm
										key={index}
										term={term}
										index={index}
										onSelect={() => {
											if (!loadingSearchResults) {
												onSelectLink(term, true);
												router.push(createSearchUrl(term, searchUrl));
											}
										}}
										onDelete={() => {
											onClickDeleteLink(term);
										}}
									/>
								))}
							</div>
						</Command.Group>
					),
					(hasCategories || hasSearchTerms) && (
						<Command.Group
							className="px-4 py-8"
							aria-label={t("accessibilitySearchTerms")}
						>
							<div className="flex flex-col gap-2 text-base font-bold text-main">
								{categories.map((category, index) => (
									<QuickSearchCategory
										key={index}
										category={category}
										index={index}
										onSelect={() => {
											if (!loadingSearchResults) {
												onSelectLink(category.name, false);
												router.push(category.url);
											}
										}}
										algoliaQueryId={searchResult.algoliaQueryId}
									/>
								))}
								{searchTerms.map((searchTerm, index) => (
									<QuickSearchSearchTerm
										key={index}
										searchTerm={searchTerm.value}
										id={searchTerm.id}
										index={index}
										onSelect={() => {
											if (!loadingSearchResults) {
												onSelectLink(searchTerm.value, true);
												router.push(
													createSearchUrl(searchTerm.value, searchUrl),
												);
											}
										}}
										algoliaQueryId={searchResult.algoliaQueryId}
									/>
								))}
							</div>
						</Command.Group>
					),
					hasProducts && (
						<Command.Group
							className="px-4"
							aria-label={t("accessibilityProducts")}
						>
							{products
								.filter((p) => p.variants.length)
								.map((product, index) => {
									const variant = product.variants[0];
									const variantUrl = getVariantUrl(
										productUrl,
										product,
										variant,
									);

									return (
										<Fragment key={index}>
											<QuickSearchProduct
												key={index}
												locale={locale}
												product={product}
												variant={variant}
												index={index}
												onSelect={() => {
													if (!loadingSearchResults) {
														onSelectProductLink();
														sendGTMEvent({
															event: "select_item",
															country: countryOf(locale).toLowerCase(),
															language: languageOf(locale).toLowerCase(),
															platform: "commercetools",
															item_list_name: "search_quick",
															items: generateItemDataFromProduct(
																availabilityT,
																product,
																1,
																variant,
																{
																	index: index + 1,
																	query_id: searchResult.algoliaQueryId,
																},
															),
														});
														router.push(variantUrl);
													}
												}}
												algoliaQueryId={searchResult.algoliaQueryId}
											/>
											{index < products.length - 1 && <hr />}
										</Fragment>
									);
								})}
						</Command.Group>
					),
					hasPages && (
						<Command.Group
							className="px-4 py-8 text-base font-bold text-main"
							heading={t("pages")}
							aria-label={t("accessibilityPages")}
						>
							<div className="mt-4 flex flex-col gap-2 text-base font-bold text-main">
								{pages.map((page, index) => (
									<QuickSearchPage
										key={index}
										page={page}
										index={index}
										algoliaQueryId={searchResult.algoliaQueryId}
										onSelect={() => {
											if (!loadingSearchResults) {
												onSelectLink(page.name, false);
												router.push(page.url);
											}
										}}
									/>
								))}
							</div>
						</Command.Group>
					),
				]
					.filter((element) => element)
					.map((element, index, array) => (
						<Fragment key={index}>
							{element}
							{index < array.length - 1 ? (
								<Command.Separator className="border-b" alwaysRender={true} />
							) : undefined}
						</Fragment>
					))}
			</>
		);
	},
);

type QuickSearchBadgeProps = {
	badge: string;
	id: string;
	index: number;
	onSelect: () => void;
	algoliaQueryId: string | null | undefined;
};

const QuickSearchBadge = memo(
	({ badge, id, index, onSelect, algoliaQueryId }: QuickSearchBadgeProps) => (
		<Command.Item
			aria-label={`Badge`}
			data-command-item
			data-insights-object-id={id}
			data-insights-position={index + 1}
			data-insights-query-id={algoliaQueryId}
			className="cursor-pointer rounded border bg-background p-1 text-center text-sm font-bold text-blue-300 hover:bg-gray-100 data-[selected='true']:bg-gray-100"
			key={index}
			value={`badge_${badge.replace(/"/g, '\\"')}_${index + 1}`}
			onSelect={onSelect}
		>
			{badge}
		</Command.Item>
	),
);

type QuickSearchRecentSearchTermProps = {
	term: string;
	index: number;
	onSelect: () => void;
	onDelete: () => void;
};

const QuickSearchRecentSearchTerm = memo(
	({ term, index, onDelete, onSelect }: QuickSearchRecentSearchTermProps) => (
		<div key={index} className="flex justify-between">
			<Command.Item
				aria-label={`Recent search term`}
				data-command-item
				className="w-full cursor-pointer font-normal text-blue-300 hover:text-main hover:underline data-[selected='true']:text-main data-[selected='true']:underline"
				key={index}
				value={`recentsearchterm_${term.replace(/"/g, '\\"')}_${index + 1}`}
				onSelect={onSelect}
			>
				<div className="pointer-events-none flex items-center p-0.5">
					<Icon icon="clock" className="mr-2" size="sm" />
					{term}
				</div>
			</Command.Item>
			<CloseButton
				variant="info"
				classes={{ button: "mt-auto md:py-0", icon: "ml-2" }}
				href="#"
				onClick={onDelete}
				tabIndex={-1}
				iconSize="sm"
			/>
		</div>
	),
);

type QuickSearchCategoryProps = {
	category: FragmentOf<typeof CategoryPageResultFragment>;
	index: number;
	onSelect: () => void;
	algoliaQueryId: string | null | undefined;
};

const QuickSearchCategory = memo(
	({ category, index, onSelect, algoliaQueryId }: QuickSearchCategoryProps) => (
		<Command.Item
			data-command-item
			data-insights-object-id={category.name}
			data-insights-position={index + 1}
			data-insights-query-id={algoliaQueryId}
			className="group flex cursor-pointer justify-between font-normal text-blue-300 hover:text-main data-[selected='true']:text-main"
			key={index}
			value={`category_${category.name.replace(/"/g, '\\"')}_${index + 1}`}
			onSelect={onSelect}
		>
			<div className="pointer-events-none flex items-center p-0.5">
				<span className="group-hover:underline group-data-[selected='true']:underline">
					{category.name}
				</span>
				{category.parent && (
					<span className="ml-2 text-sm font-normal text-muted">
						in {category.parent}
					</span>
				)}
			</div>
		</Command.Item>
	),
);

type QuickSearchSearchTermProps = {
	searchTerm: string;
	id: string;
	index: number;
	onSelect: () => void;
	algoliaQueryId: string | null | undefined;
};

const QuickSearchSearchTerm = memo(
	({
		searchTerm,
		id,
		index,
		onSelect,
		algoliaQueryId,
	}: QuickSearchSearchTermProps) => (
		<Command.Item
			aria-label={`Search term`}
			data-command-item
			data-insights-object-id={id}
			data-insights-position={index + 1}
			data-insights-query-id={algoliaQueryId}
			className="flex cursor-pointer justify-between font-normal text-blue-300 hover:text-main hover:underline data-[selected='true']:text-main data-[selected='true']:underline"
			key={index}
			value={`searchterm_${searchTerm.replace(/"/g, '\\"')}_${index + 1}`}
			onSelect={onSelect}
		>
			<div className="pointer-events-none flex items-center p-0.5">
				<span>{searchTerm}</span>
			</div>
		</Command.Item>
	),
);

type QuickSearchProductProps = {
	locale: Locale;
	product: FragmentOf<typeof CardProductFragment>;
	variant: FragmentOf<typeof CardProductVariantFragment>;
	index: number;
	onSelect: () => void;
	algoliaQueryId: string | null | undefined;
};

const QuickSearchProduct = memo(
	({
		locale,
		product,
		variant,
		index,
		onSelect,
		algoliaQueryId,
	}: QuickSearchProductProps) => {
		const t = useTranslations("QuickSearch");
		const productPriceT = useTranslations("ProductPrice");

		const hasReviews = product.reviews?.averageRating && product.reviews?.count;
		const showAccentPrice = variant?.priceBefore || variant?.discount;

		const isMultivariantProduct = product.numberOfVariants > 1;

		return (
			<Command.Item
				aria-label={"Product"}
				data-insights-object-id={variant.sku}
				data-insights-position={index + 1}
				data-insights-query-id={algoliaQueryId}
				className="group flex cursor-pointer flex-row items-center gap-4 p-4 text-base font-bold text-main"
				key={index}
				value={`product_${product.name.replace(/"/g, '\\"')}_${index + 1}`}
				onSelect={onSelect}
			>
				{product.image?.url ? (
					<Image
						src={product.image.url}
						alt={product.image.alt ?? product.name}
						width={64}
						height={40}
						sizes="64px"
						className="aspect-square object-contain"
					/>
				) : (
					<span className="aspect-[3/2] h-10 w-16 bg-gray-100" />
				)}
				<div className="flex flex-col justify-center gap-1">
					<span className="group-hover:underline group-data-[selected='true']:underline">
						{product.name}
					</span>
					<div className="flex flex-row items-center justify-start gap-2 text-xs font-normal text-muted">
						{hasReviews && (
							<Stars
								inlineMode={true}
								link={false}
								rating={product.reviews?.averageRating}
								classes={{
									text: "flex gap-2 justify-center items-center",
									parent: "items-start",
									stars: "-mr-0.5",
								}}
							>
								<span className="text-xs text-muted">
									({product.reviews?.count})
								</span>
							</Stars>
						)}
						{hasReviews ? (
							<span className="border-l pl-2">
								{t("articleNumber")} {variant?.sku}
							</span>
						) : (
							<>
								{t("articleNumber")} {variant?.sku}
							</>
						)}
					</div>
					<div>
						<div className="flex items-center gap-2 sm:grid-in-price">
							{isMultivariantProduct ? (
								<span className="text-xs font-normal text-muted">
									{productPriceT("from")}{" "}
								</span>
							) : (
								variant?.priceBefore && (
									<TaxedPrice
										price={variant.priceBefore}
										locale={locale}
										className="strike relative text-xs text-muted"
										showCurrencySign={false}
									/>
								)
							)}
							<TaxedPrice
								price={variant.price}
								locale={locale}
								className={cn(
									"text-base font-bold leading-none",
									showAccentPrice ? "text-accent" : "text-main",
								)}
								showCurrencySign={false}
							/>
						</div>
					</div>
				</div>
			</Command.Item>
		);
	},
);

type QuickSearchPageProps = {
	page: FragmentOf<typeof ContentPageResultFragment>;
	index: number;
	algoliaQueryId: string | null | undefined;
	onSelect: () => void;
};

const QuickSearchPage = memo(
	({ page, index, algoliaQueryId, onSelect }: QuickSearchPageProps) => (
		<Command.Item
			aria-label={`page_${page.name}`}
			data-command-item
			data-insights-object-id={page.name}
			data-insights-position={index + 1}
			data-insights-query-id={algoliaQueryId}
			className="flex cursor-pointer justify-between font-normal text-blue-300 hover:text-main hover:underline data-[selected='true']:text-main data-[selected='true']:underline"
			key={index}
			value={`page_${page.name.replace(/"/g, '\\"')}_${index + 1}`}
			onSelect={onSelect}
		>
			<div className="pointer-events-none flex items-center">
				<span>{page.name}</span>
			</div>
		</Command.Item>
	),
);

type QuickSearchNoResultProps = {
	value: string;
	onSelect: () => void;
};

const QuickSearchNoResult = memo(
	({ value, onSelect }: QuickSearchNoResultProps) => {
		const t = useTranslations("QuickSearch");
		return (
			<>
				<Command.Group className="px-4 py-8">
					<Command.Item
						data-search-no-result
						data-command-item
						className="group cursor-pointer"
						value={value.replace(/"/g, '\\"')}
						onSelect={onSelect}
					>
						<span className="pointer-events-none flex items-center gap-2 text-base text-blue-300 hover:text-main hover:underline group-data-[selected='true']:text-main group-data-[selected='true']:underline">
							<Icon icon={"chevron-right"} className="bg-blue-300" />
							{t("noResults", {
								value,
							})}
						</span>
					</Command.Item>
				</Command.Group>
			</>
		);
	},
);

const createSearchUrl = (value: string, searchUrl: string) => {
	const searchParams = new URLSearchParams();
	searchParams.set("q", value);
	return `${searchUrl}?${searchParams.toString()}`;
};

QuickSearch.displayName = "QuickSearch";
QuickSearchBadge.displayName = "QuickSearchBadge";
QuickSearchRecentSearchTerm.displayName = "QuickSearchRecentSearchTerm";
QuickSearchCategory.displayName = "QuickSearchCategory";
QuickSearchSearchTerm.displayName = "QuickSearchSearchTerm";
QuickSearchProduct.displayName = "QuickSearchProduct";
QuickSearchPage.displayName = "QuickSearchPage";
QuickSearchNoResult.displayName = "QuickSearchNoResult";
